import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";
import type { RouteName } from "../navigation/navigation.types";

const ROUTE_ANNOUNCEMENTS: Partial<Record<RouteName, string>> = {
  Announcement: "Announcements",
  "Announcement Detail": "Announcement Details",
  "Chat Settings": "Chat Settings",
  "Companion Chat": "Companion Chat",
  "Companion Home": "Companion Home",
  Coursework: "Coursework",
  "Custom Assignment Student": "Assignment",
  "Custom Course AITutor": "AI Tutor",
  "Custom Course Assessment": "Assessment",
  "Custom Resource Student": "Resource",
  "Custom School Student Courses": "Student Courses",
  Insights: "Insights",
  "Interactive Learning Environment": "Interactive Learning Environment",
  "Learning Activities": "Learning Activities",
  Login: "Login",
  "To do": "To Do",
};

const FOCUS_DELAY_MS = 100;

function getAnnouncementText(routeName: RouteName): string {
  return ROUTE_ANNOUNCEMENTS[routeName] ?? routeName;
}

function focusMainContent(): void {
  const mainContent =
    document.getElementById("main-content") ?? (document.querySelector('[role="main"]') as HTMLElement | null);

  if (!mainContent) {
    return;
  }

  if (!mainContent.hasAttribute("tabindex")) {
    mainContent.setAttribute("tabindex", "-1");
  }

  mainContent.focus();
}

export function useRouteAnnouncer(routeName: RouteName | null): { announcement: string } {
  const previousRouteRef = useRef<RouteName | null>(null);
  const announcementTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (Platform.OS !== "web" || !routeName) {
      return;
    }

    const previousRoute = previousRouteRef.current;
    previousRouteRef.current = routeName;

    if (!previousRoute || previousRoute === routeName) {
      return;
    }

    const nextAnnouncement = getAnnouncementText(routeName);
    setAnnouncement("");

    if (announcementTimeoutRef.current) {
      clearTimeout(announcementTimeoutRef.current);
    }

    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
    }

    announcementTimeoutRef.current = setTimeout(() => {
      setAnnouncement(nextAnnouncement);
    }, 0);

    focusTimeoutRef.current = setTimeout(() => {
      focusMainContent();
    }, FOCUS_DELAY_MS);

    return () => {
      if (announcementTimeoutRef.current) {
        clearTimeout(announcementTimeoutRef.current);
        announcementTimeoutRef.current = null;
      }

      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
    };
  }, [routeName]);

  return { announcement };
}

export default useRouteAnnouncer;
